import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GlassPage } from '../components/chrome';
import { PageHero } from '../components/ui';
import { postJSON } from '../lib/api';

const Contact = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organizationType: '',
    subject: location.state?.subject || '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [emailError, setEmailError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email.includes('@')) {
      setEmailError('Please enter a valid email address, like name@example.com.');
      document.getElementById('contact-email')?.focus();
      return;
    }
    setEmailError('');

    setIsSubmitting(true);
    setSubmitError(false);
    try {
      await postJSON('/contact', {
        name: formData.name,
        email: formData.email,
        organizationType: formData.organizationType || 'N/A',
        subject: formData.subject || 'Website contact',
        message: formData.message,
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error(err);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const inputClass = 'glass-input w-full px-5 py-3.5 md:px-6 md:py-4';
  const labelClass = 'block text-[10px] font-bold uppercase tracking-widest mb-2.5 text-zinc-500';

  return (
    <GlassPage>
      <PageHero
        title={<>Contact<br/>Us</>}
        lead="Have a question or want to get in touch? Send us a message and we'll get back to you as soon as possible."
      />

      <section className="px-4 md:px-6 pb-12 md:pb-16">
        <div className="glass max-w-4xl mx-auto px-5 sm:px-8 md:px-12 py-10 md:py-16">
          {isSubmitted ? (
            <div role="status" className="rounded-3xl border border-green-700 bg-green-500/10 p-8 text-center">
              <h2 className="text-2xl font-bold mb-4 text-green-800">Message Sent Successfully!</h2>
              <p className="text-zinc-600">Thank you for contacting us. We'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
              <div>
                <label htmlFor="contact-name" className={labelClass}>Name *</label>
                <input
                  id="contact-name"
                  autoComplete="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className={labelClass}>Email *</label>
                <input
                  id="contact-email"
                  autoComplete="email"
                  aria-invalid={!!emailError}
                  aria-describedby={emailError ? 'contact-email-error' : undefined}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="your@email.com"
                />
                {emailError && (
                  <p id="contact-email-error" role="alert" className="mt-2 text-sm font-bold text-red-700">Error: {emailError}</p>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact-org" className={labelClass}>Organization *</label>
                  <select
                    id="contact-org"
                    name="organizationType"
                    value={formData.organizationType}
                    onChange={handleChange}
                    required
                    className={`${inputClass} appearance-none cursor-pointer`}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2318181b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 1.25rem center'
                    }}
                  >
                    <option value="" disabled>Select type</option>
                    <option value="individual">Individual</option>
                    <option value="investor">Investor</option>
                    <option value="company">Company Representative</option>
                    <option value="partner">Potential Partner</option>
                    <option value="media">Media/Press</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contact-subject" className={labelClass}>Subject *</label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="What is this about?"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-message" className={labelClass}>Message *</label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className={`${inputClass} resize-none`}
                  placeholder="Tell us what you'd like to discuss..."
                />
              </div>

              <p className="text-sm text-zinc-600">
                We use what you send to reply to you. See our <Link to="/privacy" className="underline underline-offset-2">Privacy Policy</Link>.
              </p>

              <button type="submit" disabled={isSubmitting} className={`btn-dark w-full md:w-auto md:px-16 ${isSubmitting ? 'opacity-60 cursor-wait' : ''}`}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitError && (
                <p role="alert" className="text-sm text-red-700 font-bold">
                  Error: your message was not sent. Please try again or email support@korsa.tech.
                </p>
              )}
            </form>
          )}
        </div>
      </section>
    </GlassPage>
  );
};

export default Contact;
